// maths.portll.net — Firestore data helpers for the Grown-Ups' Corner.
//
// The single funnel between the parents-section UI and Firestore. Every write here
// is shaped to PASS firestore.rules (see isValidChild / isValidCompletion /
// isValidDataset) — the rules are the wall, these helpers are the polite front door.
//
// Trust boundary: request.auth.uid. A client can only ever touch children of its OWN
// parent account, so these paths never take a "which parent" beyond the caller's uid.
//
// Query note: `tasks` documents may omit the optional `sort` field, so we must NOT
// orderBy('sort') server-side (Firestore drops docs missing the ordered field). We
// query with equality filters only (no composite index needed) and order client-side,
// mirroring scripts/merge-content.mjs's deterministic ordering.

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase.js";
import { validateDataset } from "./datasetShapes.js";

// Keep in step with scripts/merge-content.mjs strandOrder.
const STRAND_ORDER = ["number", "algebra", "measurement", "space", "statistics", "probability"];

const childrenPath = (uid) => `users/${uid}/children`;
const childPath = (uid, childId) => `users/${uid}/children/${childId}`;
const completionsPath = (uid, childId) => `${childPath(uid, childId)}/completions`;
const datasetsPath = (uid, childId) => `${childPath(uid, childId)}/datasets`;

/** Foundation shows as "F"; every other year as its number. */
export function yearLabel(yearLevel) {
  const y = Number(yearLevel);
  return y === 0 ? "F" : String(y);
}

// ── content: published tasks for a year ──────────────────────────────────────
/**
 * All published tasks for one year level, ordered like the print/build pipeline
 * (strand, then optional `sort`, then id). Equality-only query → no composite index.
 * @param {number} yearLevel 0 (Foundation) … 10
 * @returns {Promise<Array<Object>>}
 */
export async function fetchPublishedTasksForYear(yearLevel) {
  const q = query(
    collection(db, "tasks"),
    where("status", "==", "published"),
    where("yearLevel", "==", Number(yearLevel))
  );
  const snap = await getDocs(q);
  const tasks = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  tasks.sort(
    (a, b) =>
      (STRAND_ORDER.indexOf(a.strand) - STRAND_ORDER.indexOf(b.strand)) ||
      ((a.sort ?? 0) - (b.sort ?? 0)) ||
      String(a.title || a.id).localeCompare(String(b.title || b.id))
  );
  return tasks;
}

// ── live family subscriptions ────────────────────────────────────────────────
/**
 * Live completions for a child, newest first. Single-field orderBy (auto-indexed).
 * @returns {() => void} unsubscribe
 */
export function subscribeCompletions(uid, childId, cb) {
  const q = query(collection(db, completionsPath(uid, childId)), orderBy("completedAt", "desc"));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error("subscribeCompletions", err)
  );
}

/**
 * Live hand-collected datasets for a child, newest first.
 * @returns {() => void} unsubscribe
 */
export function subscribeDatasets(uid, childId, cb) {
  const q = query(collection(db, datasetsPath(uid, childId)), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error("subscribeDatasets", err)
  );
}

// ── completions ──────────────────────────────────────────────────────────────
/**
 * A fresh completion doc ref with a generated id — so photos can be uploaded under
 * the id (storage path) BEFORE the doc itself is written.
 * @returns {{ id: string, ref: import("firebase/firestore").DocumentReference }}
 */
export function newCompletionRef(uid, childId) {
  const ref = doc(collection(db, completionsPath(uid, childId)));
  return { id: ref.id, ref };
}

/**
 * Write a completion (attestation record). `attestation` MUST be true — the honesty
 * contract; the rules also reject anything else. Pass `completionId` to write to a
 * ref you pre-minted with newCompletionRef (so photoIds already point at it).
 * @returns {Promise<string>} the completion id
 */
export async function logCompletion(
  uid,
  childId,
  { taskId, taskVersion, attestation, rubricTicks, photoIds, datasetId, parentNote },
  completionId
) {
  if (attestation !== true) {
    throw new Error("logCompletion: attestation must be true (the “we did this together” contract).");
  }
  const ref = completionId
    ? doc(db, `${completionsPath(uid, childId)}/${completionId}`)
    : doc(collection(db, completionsPath(uid, childId)));

  const payload = {
    taskId: String(taskId),
    taskVersion: Number.isFinite(taskVersion) ? Math.trunc(taskVersion) : 1,
    attestation: true,
    completedAt: serverTimestamp(),
  };
  if (Array.isArray(rubricTicks)) payload.rubricTicks = rubricTicks;
  if (Array.isArray(photoIds) && photoIds.length) payload.photoIds = photoIds.slice(0, 3);
  if (datasetId) payload.datasetId = String(datasetId);
  if (parentNote && String(parentNote).trim()) payload.parentNote = String(parentNote).slice(0, 1000);

  await setDoc(ref, payload);
  return ref.id;
}

// ── datasets ─────────────────────────────────────────────────────────────────
const DEFAULT_DATASET_NAME = {
  categorical_tally: "Our tally",
  trial_outcomes: "Our experiment",
  numerical_list: "Our measurements",
  two_sample: "Our two groups",
  bivariate_pairs: "Our pairs",
};

/**
 * Validate (via datasetShapes.validateDataset) then write a hand-collected dataset.
 * @returns {Promise<string>} the dataset id
 */
export async function saveDataset(uid, childId, { kind, name, data, taskId, collectedOn }) {
  const { ok, errors } = validateDataset(kind, data);
  if (!ok) throw new Error(`saveDataset: ${errors.join("; ")}`);

  const cleanName = (name && String(name).trim()) || DEFAULT_DATASET_NAME[kind] || "Our data";
  const ref = doc(collection(db, datasetsPath(uid, childId)));
  const payload = {
    kind,
    name: cleanName.slice(0, 80),
    data,
    collectedOn: collectedOn || new Date().toISOString().slice(0, 10),
    createdAt: serverTimestamp(),
  };
  if (taskId) payload.taskId = String(taskId);

  await setDoc(ref, payload);
  return ref.id;
}

/** Remove a hand-collected dataset. */
export function deleteDataset(uid, childId, datasetId) {
  return deleteDoc(doc(db, `${datasetsPath(uid, childId)}/${datasetId}`));
}

// ── child profiles ───────────────────────────────────────────────────────────
/**
 * Create a child profile under the parent. yearLevel is clamped to 0–10 (0 = F).
 * @returns {Promise<string>} the child id
 */
export async function createChild(uid, { username, avatarId, yearLevel }) {
  // Cloud identity is a non-PII username (handle). The real name, if any, is a
  // LOCAL-ONLY display override (see ProfileContext localName helpers) — never stored here.
  const handle = String(username || "").trim();
  if (!handle) throw new Error("createChild: a username is required.");
  const ref = doc(collection(db, childrenPath(uid)));
  await setDoc(ref, {
    username: handle.slice(0, 30),
    avatarId: String(avatarId || "").slice(0, 40),
    yearLevel: Math.max(0, Math.min(10, Math.trunc(Number(yearLevel) || 0))),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Patch a child profile (merge). Known numeric/string fields are coerced so the
 * merged doc still passes isValidChild. Never write a real name here — that stays local.
 */
export function updateChild(uid, childId, patch) {
  const clean = { ...patch };
  if ("yearLevel" in clean) clean.yearLevel = Math.max(0, Math.min(10, Math.trunc(Number(clean.yearLevel) || 0)));
  if ("username" in clean) clean.username = String(clean.username || "").trim().slice(0, 30);
  if ("avatarId" in clean) clean.avatarId = String(clean.avatarId || "").slice(0, 40);
  return setDoc(doc(db, childPath(uid, childId)), clean, { merge: true });
}
